import axios from "@/plugins/axios";

////////////////////////////////////////////////////////////////////////////////
// 출금 모듈 (씨드페이·쿠콘·바움) 콘솔 API
//
// 출금 잔액·보류건 API 를 부르는 곳은 여기 하나뿐이다.
// 백엔드 응답 필드 이름이 바뀌면 이 파일만 고친다. 화면은 여기서 돌려주는
// 모양만 쓴다.
//
// 기존 /api/console/seedpay/* 는 백엔드에 같은 처리로 남아 있다(seedpay.js).
////////////////////////////////////////////////////////////////////////////////

let url = "/api/console/payout";

// 출금 모듈 이름. value 는 백엔드가 돌려주는 module 값이다.
export const MODULES = [
    { value: "seedpay", text: "씨드페이" },
    { value: "coocon", text: "쿠콘" },
    { value: "baum", text: "바움" },
];

export function moduleText(value){
    let found = MODULES.find(module => module.value == value);
    return found ? found.text : (value || "-");
}

export default {
    MODULES,
    moduleText,

    // 잔액 → { module, amount, label }  (서버 응답: { success, module, balance, label })
    // 서버에 지금 설정된 모듈의 잔액이다.
    // amount 는 숫자 또는 null(알 수 없음). 0원과 구분해서 보여준다.
    getBalance(){
        return axios.get(`${url}/balance`).then(result => ({
            module: result.data.module,
            amount: result.data.balance ?? null,
            label: result.data.label,
        }));
    },

    // 보류(결과 미확정)·실패 출금건 목록 → [Exchange]
    // 오래 멈춘 preparing·sending·rollback 은 stale: true 로 온다. 복구 중 멈춘
    // restoring 과, 바움 통보가 어긋난 확정 건(pgNoticeConflict: true, success·
    // restored 포함)도 함께 온다.
    // 회원 캐시를 깎아 둔(cashHold) 건에만 cashHeld(boolean)가 붙는다. false 면 캐시가
    // 이미 회원에게 돌아간 건이다. 없으면(undefined) 예전 건이라 알 수 없다.
    // pgRollbackFailed 건의 pgRollbackFrom 은 "preparing"(차감 중 멈춘 건을 정리,
    // 송금 요청 전) | "rollback"(즉시 롤백 중 되돌림 실패). 예전 건은 없다.
    getPendings(params){
        return axios.get(`${url}/pendings`, { params }).then(result => result.data.pendings || []);
    },

    // 결과조회 — 상태만 갱신한다. → { pgStatus, applied, status, resultCd, resultMsg }
    // applied 가 false 면 그 사이 다른 처리(스케줄러·웹훅·사람)로 상태가 바뀌어
    // 이번 조회 결과는 반영되지 않은 것이다.
    // 전송 중 응답 없음(stale sending) 건은 상태를 바꾸지 않고 { applied:false, pgStatus:"sending", status, … }
    // 로 온다. 이때 status 가 보낸 곳의 답이다. 거래번호가 없으면 서버가 거부한다.
    postCheck(_exchange){
        return axios.post(`${url}/pendings/${_exchange}/check`).then(result => {
            let data = result.data.result;
            return {
                pgStatus: data.pgStatus,
                applied: data.applied,
                status: data.status,
                resultCd: data.resultCd,
                resultMsg: data.resultMsg,
            };
        });
    },

    // 사람이 결과를 정한다. status: "success" | "failed"
    postDecide(_exchange, status){
        return axios.post(`${url}/pendings/${_exchange}/decide`, { status }).then(result => result.data.result);
    },

    // 캐시 복구 — 실제로 캐시가 움직이는 호출이다. → { restored, cash, alreadyRestored, cancelLedgerFailed }
    // cancelLedgerFailed 가 true 면 캐시는 돌려줬지만 취소 원장 저장만 실패한 것이다(예전 방식 건).
    // 관리자 출금(origin "console") 건은 캐시를 더하지 않고 상태만 닫는다(restored 0).
    // 복구 중 멈춘(restoring) 건도 같은 호출로 이어서 한다.
    // alreadyRestored 가 true 면 즉시 롤백이 이미 캐시를 돌려준 건이라 캐시는
    // 더하지 않고 기록만 닫은 것이다.
    postRestore(_exchange){
        return axios.post(`${url}/pendings/${_exchange}/restore`).then(result => ({
            restored: result.data.result.restored,
            cash: result.data.result.cash,
            alreadyRestored: !!result.data.result.alreadyRestored,
            cancelLedgerFailed: !!result.data.result.cancelLedgerFailed,
        }));
    },

    // 멈춘 preparing·rollback 건 정리 — 돈은 움직이지 않는다. → { result: "failed" | "cleaned" }
    // failed: 캐시가 아직 안 돌아간 건이라 송금실패로 바꿨다(캐시 복구를 따로 눌러야 한다).
    // cleaned: 캐시가 이미 돌아갔거나 깎인 적이 없는 건이라 기록을 지웠다.
    postResolve(_exchange){
        return axios.post(`${url}/pendings/${_exchange}/resolve`).then(result => ({
            result: result.data.result.result,
        }));
    },

    // 바움 통보가 어긋난 확정 건의 경고를 끈다. 상태·캐시는 그대로다.
    postAcknowledge(_exchange){
        return axios.post(`${url}/pendings/${_exchange}/acknowledge`).then(result => result.data.result);
    },
}
